import { Link } from 'react-router-dom';
import type { BlogPost } from '@/mocks/blog';

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}

const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
  web: { bg: 'rgba(30,111,255,0.1)', text: '#1E6FFF', border: 'rgba(30,111,255,0.25)' },
  sns: { bg: 'rgba(0,153,255,0.1)', text: '#0099FF', border: 'rgba(0,153,255,0.25)' },
  seo: { bg: 'rgba(0,180,120,0.1)', text: '#00B478', border: 'rgba(0,180,120,0.25)' },
  meo: { bg: 'rgba(0,180,120,0.1)', text: '#00B478', border: 'rgba(0,180,120,0.25)' },
  marketing: { bg: 'rgba(255,140,0,0.1)', text: '#FF8C00', border: 'rgba(255,140,0,0.25)' },
  ai: { bg: 'rgba(120,80,255,0.1)', text: '#7850FF', border: 'rgba(120,80,255,0.25)' },
};

const categoryLabels: Record<string, string> = {
  web: 'WEB制作',
  sns: 'SNS運用',
  seo: 'SEO・MEO',
  meo: 'MEO対策',
  marketing: 'マーケティング',
  ai: 'AI活用',
};

export default function BlogCard({ post, featured = false }: BlogCardProps) {
  const color = categoryColors[post.category] ?? categoryColors.web;

  if (featured) {
    return (
      <Link
        to={`/blog/${post.slug}`}
        className="group block rounded-2xl overflow-hidden bg-white cursor-pointer"
        style={{ border: '1px solid rgba(30,111,255,0.1)' }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative h-64 lg:h-auto overflow-hidden">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10" />
          </div>
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-5">
              <span
                className="text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap"
                style={{ background: color.bg, color: color.text, border: `1px solid ${color.border}` }}
              >
                {categoryLabels[post.category]}
              </span>
              <span className="text-[#999] text-xs">{post.publishedAt}</span>
              <span className="text-[#999] text-xs">{post.readingTime}分で読める</span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-[#0A1628] leading-snug mb-4 group-hover:text-[#1E6FFF] transition-colors">
              {post.title}
            </h2>
            <p className="text-[#666] text-sm leading-relaxed mb-6 line-clamp-3">
              {post.excerpt}
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.map((tag) => (
                <span key={tag} className="text-xs text-[#999] bg-[#F5F7FA] px-2.5 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 text-[#1E6FFF] text-sm font-semibold">
              続きを読む
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block rounded-2xl overflow-hidden bg-white cursor-pointer"
      style={{ border: '1px solid rgba(30,111,255,0.1)' }}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute top-4 left-4">
          <span
            className="text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap backdrop-blur-sm"
            style={{ background: color.bg, color: color.text, border: `1px solid ${color.border}` }}
          >
            {categoryLabels[post.category]}
          </span>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[#999] text-xs">{post.publishedAt}</span>
          <span className="text-[#ccc]">·</span>
          <span className="text-[#999] text-xs">{post.readingTime}分で読める</span>
        </div>
        <h3 className="text-base font-bold text-[#0A1628] leading-snug mb-3 group-hover:text-[#1E6FFF] transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-[#666] text-sm leading-relaxed line-clamp-2 mb-4">
          {post.excerpt}
        </p>
        <div className="flex items-center gap-2 text-[#1E6FFF] text-sm font-semibold">
          続きを読む
          <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
}
